import { ArrowUpRight, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { FadeUp, Overline } from "@/components/Motion";
import { PRODUCTS, SITE, buildWhatsAppLink } from "@/data/site";
import { useLang, TA, u } from "@/data/i18n";

export default function Products() {
  const { lang } = useLang();
  return (
    <section id="products" data-testid="products-section" className="py-24 md:py-32 bg-white border-y border-ink/10">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 mb-16">
          <div className="md:col-span-6">
            <FadeUp>
              <Overline>{u(lang, "products_overline")}</Overline>
              <h2 className="mt-4 font-display font-black tracking-tight text-4xl md:text-5xl text-leaf leading-[1.02]">
                {u(lang, "products_heading")}
              </h2>
            </FadeUp>
          </div>
          <div className="md:col-span-6 flex items-end justify-start md:justify-end">
            <FadeUp delay={0.15}>
              <a
                data-testid="catalogue-indiamart-link"
                href={SITE.indiamart}
                target="_blank"
                rel="noreferrer"
                className="group inline-flex items-center gap-2 border-b-2 border-leaf pb-1 font-bold text-leaf tracking-wide transition-colors duration-300 hover:text-leaf-bright hover:border-sun"
              >
                {u(lang, "indiamart_link")}
                <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
            </FadeUp>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6">
          {PRODUCTS.map((p, i) => (
            <FadeUp key={p.id} delay={0.06 * i} className={`${p.span} group h-full`}>
              <article
                data-testid={`product-${p.id}`}
                className="relative h-full min-h-[380px] md:min-h-[420px] border border-ink/10 overflow-hidden bg-paper flex flex-col"
              >
                <div className={`relative w-full overflow-hidden ${p.span.includes("row-span-2") ? "flex-1 min-h-[320px]" : "aspect-square"}`}>
                  <img
                    src={p.img}
                    alt={`${p.name} — Lovely Foodpacks`}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <span className="absolute top-4 left-4 bg-sun text-leaf text-[10px] font-bold tracking-[0.2em] uppercase px-3 py-1.5">
                    {lang === "ta" ? TA.products[p.id].tag : p.tag}
                  </span>
                  <span className="pointer-events-none absolute inset-0 border-[3px] border-sun opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                </div>
                  <div className="p-6 md:p-7 bg-white border-t border-ink/10">
                    <h3 className="font-display font-bold text-xl md:text-2xl text-leaf tracking-tight">
                      {lang === "ta" ? TA.products[p.id].name : p.name}
                    </h3>
                    <p className="mt-2 text-sm md:text-base text-muted leading-relaxed">
                      {lang === "ta" ? TA.products[p.id].desc : p.desc}
                    </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {p.points.map((pt) => (
                      <span key={pt} className="text-[11px] font-semibold tracking-wide uppercase border border-leaf/20 text-leaf px-2.5 py-1">
                        {pt}
                      </span>
                    ))}
                  </div>
                  <div className="mt-5 flex flex-wrap items-center gap-5">
                    <Link
                      data-testid={`product-spec-${p.id}`}
                      to={`/product/${p.id}`}
                      className="inline-flex items-center gap-2 text-sm font-bold text-leaf border-b-2 border-leaf pb-0.5 transition-colors duration-300 hover:text-leaf-bright hover:border-sun"
                    >
                      <FileText size={15} /> {u(lang, "view_spec")}
                    </Link>
                    <a
                      data-testid={`product-enquire-${p.id}`}
                      href={buildWhatsAppLink(`Hello Lovely Foodpacks! I'm interested in ${p.name}. Please share pricing & availability.`)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-bold text-leaf border-b-2 border-sun pb-0.5 transition-colors duration-300 hover:text-leaf-bright"
                    >
                      {u(lang, "enquire_whatsapp")} <ArrowUpRight size={15} />
                    </a>
                  </div>
                </div>
              </article>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}
